/**
 * What Ory says happens next when a flow finishes without issuing a session
 * (#60).
 *
 * # The gap this closes
 *
 * `AuthPage` handled two outcomes: a flow (render it) and anything else (ask
 * who we are, and let the router act). Registration has a third. When the
 * tenant does not issue a session at registration — because it requires email
 * verification first — Ory answers with `identity` and a **`continue_with`**
 * array. That is not a flow, so nothing was re-rendered; and it carries no
 * session, so `refresh()` changed nothing and the redirect never fired. The
 * form cleared its messages, redrew itself unchanged, and said nothing, while
 * the customer had in fact been registered and needed to go and look at their
 * inbox.
 *
 * # It is not reachable today, and that is the point
 *
 * The tenant currently issues a session at registration — verified by
 * registering an identity against staging, which returned one. So every test
 * is green and the branch is dead. That is the same shape as #40: correct
 * against the tenant's CURRENT configuration, silently wrong against a change
 * nobody would think to re-test a frontend for. Enabling required verification
 * is one checkbox in the Ory console, made by whoever owns identity policy.
 *
 * # Decoded, not cast
 *
 * The entries are a discriminated union in `@ory/client-fetch`
 * (`ContinueWith`), and this reads the two that matter to a browser app. An
 * entry it does not know is IGNORED rather than rendered — a bundle can be
 * months older than the tenant, and `undefined` on a screen is worse than
 * silence about a step that does not concern us.
 */

/** The subset a browser app can act on. */
export type Continuation =
  /** Ory wants the browser somewhere else. */
  | { readonly kind: 'redirect'; readonly to: string }
  /** The customer must confirm an email address before anything else happens. */
  | { readonly kind: 'verify'; readonly address: string; readonly flowId: string }

interface Entry {
  action?: unknown
  redirect_browser_to?: unknown
  flow?: { id?: unknown; verifiable_address?: unknown }
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined
}

/**
 * The first continuation this app can act on, or null.
 *
 * First rather than all: they are ordered, and acting on two at once would mean
 * navigating twice. Redirect wins where both appear, because it is Ory telling
 * the browser where to be rather than telling us what to say.
 */
export function continuationOf(value: unknown): Continuation | null {
  if (typeof value !== 'object' || value === null) return null
  const list = (value as { continue_with?: unknown }).continue_with
  if (!Array.isArray(list)) return null

  let verify: Continuation | null = null

  for (const raw of list as Entry[]) {
    if (typeof raw !== 'object' || raw === null) continue

    if (raw.action === 'redirect_browser_to') {
      const to = str(raw.redirect_browser_to)
      if (to !== undefined) return { kind: 'redirect', to }
      continue
    }

    if (raw.action === 'show_verification_ui' && verify === null) {
      const address = str(raw.flow?.verifiable_address)
      const flowId = str(raw.flow?.id)
      // Both, or neither. A "check your email" with no address to name is a
      // sentence the customer cannot act on, and this app does not invent one.
      if (address !== undefined && flowId !== undefined) {
        verify = { kind: 'verify', address, flowId }
      }
    }
    // Everything else — show_settings_ui, show_recovery_ui,
    // set_ory_session_token, the device-authorisation pin — is not this page's
    // business and is passed over rather than guessed at.
  }

  return verify
}
